import { http } from "msw";
import type {
  DashboardAnalyticsPoint,
  DashboardCustomer,
  DashboardRange,
  Order,
  Product,
  ProductVisibility,
} from "../../api/types";
import { getCollection } from "../db";
import { aggregateDashboard, DAY_MS, RANGE_DAYS } from "../engine";
import * as viewerState from "../viewer-state";
import { decorateCreatorSummary, decorateProductSummary, findViewerStoreSlug, mockJson, notFound, runMutation } from "./shared";

function parseRange(url: URL): DashboardRange {
  const range = url.searchParams.get("range");
  return range === "7d" || range === "30d" || range === "90d" ? range : "30d";
}

/** "Now" is the freshest order timestamp, not the wall clock — keeps the
 * dashboard populated regardless of when the prototype happens to be opened
 * relative to the seed's fixed 2026-09-01 reference date. */
function resolveNow(orders: Order[]): number {
  if (orders.length === 0) return Date.now();
  return Math.max(...orders.map((o) => Date.parse(o.createdAt)));
}

async function storeOrders(): Promise<Order[]> {
  const storeSlug = await findViewerStoreSlug();
  const orders = await getCollection<Order>("orders");
  return orders.filter((o) => o.storeSlug === storeSlug);
}

export const dashboardHandlers = [
  http.get("/api/dashboard", async ({ request }) => {
    const url = new URL(request.url);
    const range = parseRange(url);
    const orders = await storeOrders();
    const payload = aggregateDashboard(orders, range, resolveNow(orders));
    return mockJson({
      ...payload,
      recentOrders: payload.recentOrders.map((order) => ({
        ...order,
        buyer: decorateCreatorSummary(order.buyer),
        product: decorateProductSummary(order.product),
      })),
      topProducts: payload.topProducts.map((product) => ({ ...decorateProductSummary(product), revenue: product.revenue, unitsSold: product.unitsSold })),
    });
  }),

  http.get("/api/dashboard/products", async ({ request }) => {
    const url = new URL(request.url);
    const range = parseRange(url);
    const storeSlug = await findViewerStoreSlug();
    const [products, orders] = await Promise.all([getCollection<Product>("products"), storeOrders()]);
    const mine = products.filter((p) => p.storeSlug === storeSlug);

    const now = resolveNow(orders);
    const windowStart = now - RANGE_DAYS[range] * DAY_MS;
    const inRange = orders.filter((o) => o.status === "completed" && Date.parse(o.createdAt) >= windowStart && Date.parse(o.createdAt) <= now);

    const rows = mine.map((product) => {
      const productOrders = inRange.filter((o) => o.product.id === product.id);
      return {
        ...decorateProductSummary(product),
        visibility: viewerState.productVisibility(product.id),
        unitsSold: productOrders.length,
        revenue: productOrders.reduce((sum, o) => sum + o.amount, 0),
      };
    });
    return mockJson(rows);
  }),

  http.get("/api/dashboard/customers", async ({ request }) => {
    const url = new URL(request.url);
    const range = parseRange(url);
    const orders = await storeOrders();
    const now = resolveNow(orders);
    const windowStart = now - RANGE_DAYS[range] * DAY_MS;
    const inRange = orders.filter((o) => Date.parse(o.createdAt) >= windowStart && Date.parse(o.createdAt) <= now);

    const byBuyer = new Map<string, DashboardCustomer>();
    for (const order of inRange) {
      const existing = byBuyer.get(order.buyer.id);
      if (existing) {
        existing.orders += 1;
        existing.totalSpent += order.status === "completed" ? order.amount : 0;
        if (Date.parse(order.createdAt) > Date.parse(existing.lastOrderAt)) existing.lastOrderAt = order.createdAt;
      } else {
        byBuyer.set(order.buyer.id, {
          buyer: decorateCreatorSummary(order.buyer),
          orders: 1,
          totalSpent: order.status === "completed" ? order.amount : 0,
          lastOrderAt: order.createdAt,
        });
      }
    }
    const customers = [...byBuyer.values()].sort((a, b) => b.totalSpent - a.totalSpent);
    return mockJson(customers);
  }),

  http.get("/api/dashboard/analytics", async ({ request }) => {
    const url = new URL(request.url);
    const range = parseRange(url);
    const orders = await storeOrders();
    const now = resolveNow(orders);
    const days = RANGE_DAYS[range];

    const points: DashboardAnalyticsPoint[] = Array.from({ length: days }, (_, i) => {
      const daysAgo = days - 1 - i;
      const dayStart = now - (daysAgo + 1) * DAY_MS;
      const dayEnd = now - daysAgo * DAY_MS;
      const dayOrders = orders.filter((o) => {
        const t = Date.parse(o.createdAt);
        return t >= dayStart && t < dayEnd;
      }).length;
      // No separate traffic model exists in this mock — visitors/views are a
      // deterministic function of order volume so the analytics chart is
      // stable and internally consistent rather than randomly generated.
      const visitors = dayOrders * 9 + 40;
      const views = visitors * 2 + dayOrders * 3;
      const conversionRate = visitors === 0 ? 0 : Math.round((dayOrders / visitors) * 10000) / 100;
      return { date: new Date(dayEnd).toISOString().slice(0, 10), views, visitors, conversionRate };
    });
    return mockJson(points);
  }),

  http.patch("/api/dashboard/products/:id", async ({ request, params }) => {
    const { visibility } = (await request.json()) as { visibility: ProductVisibility };
    const products = await getCollection<Product>("products");
    const product = products.find((p) => p.id === params.id);
    if (!product) return notFound("Product not found");
    return runMutation(() => {
      viewerState.setProductVisibility(String(params.id), visibility);
      return { ...decorateProductSummary(product), visibility };
    });
  }),
];
